import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Github, Linkedin, Send, ChevronRight, ExternalLink, Code2, Smartphone, Globe, Database, Palette, Wrench, RefreshCw, Building2, Award } from "lucide-react";

/* ─────────────────────────────────────────────
   PROJECT CARD  — horizontal "case study" style
───────────────────────────────────────────── */
function ProjectCard({ project, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
    >
      <Link to={`/project/${project.folder}`} className="group block">
        <div className="relative flex flex-col sm:flex-row gap-0 rounded-2xl overflow-hidden
          bg-white/[0.03] border border-white/10
          hover:border-cyan-400/30 hover:bg-white/[0.05]
          transition-all duration-350
          hover:shadow-[0_8px_40px_-10px_rgba(56,189,248,0.18)]">

          {/* ── image panel ── */}
          <div className="relative sm:w-52 md:w-60 flex-shrink-0 overflow-hidden bg-[#080a16]">
            <img
              src={require(`./assets/${project.folder}/1.png`)}
              alt={project.name}
              className="w-full h-48 sm:h-full object-cover object-top
                transition-transform duration-500 group-hover:scale-105"
            />
            {/* subtle overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#080a16]/60 hidden sm:block" />
            {/* screenshot count badge */}
            <div className="absolute top-3 left-3 font-mono text-[10px] font-semibold
              bg-black/60 border border-white/10 text-slate-300 px-2 py-0.5 rounded-md
              backdrop-blur-sm">
              {project.count} screenshots
            </div>
          </div>

          {/* ── live-site pill (opens the real product) ── */}
          {project.live && (
            <span
              role="link"
              tabIndex={0}
              title={`Open ${project.live}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(project.live, "_blank", "noopener,noreferrer");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(project.live, "_blank", "noopener,noreferrer");
                }
              }}
              className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5
                text-[10px] font-semibold px-2.5 py-1 rounded-md cursor-pointer
                bg-emerald-400/10 border border-emerald-400/30 text-emerald-300
                backdrop-blur-sm hover:bg-emerald-400/20 hover:border-emerald-400/50
                transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {project.live.replace(/^https?:\/\//, "")}
              <ExternalLink size={10} />
            </span>
          )}

          {/* ── Google Play pill (opens the store listing) ── */}
          {project.playStore && (
            <span
              role="link"
              tabIndex={0}
              title="Get it on Google Play"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(project.playStore, "_blank", "noopener,noreferrer");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(project.playStore, "_blank", "noopener,noreferrer");
                }
              }}
              className={`absolute ${project.live ? "top-10" : "top-3"} right-3 z-10 inline-flex items-center gap-1.5
                text-[10px] font-semibold px-2.5 py-1 rounded-md cursor-pointer
                bg-emerald-400/10 border border-emerald-400/30 text-emerald-300
                backdrop-blur-sm hover:bg-emerald-400/20 hover:border-emerald-400/50
                transition-colors`}
            >
              <Smartphone size={10} />
              Google Play
              <ExternalLink size={10} />
            </span>
          )}

          {/* ── content panel ── */}
          <div className="flex flex-col flex-1 p-5 md:p-6">
            {/* top row */}
            <div className="flex items-start justify-between gap-3 mb-2">
              <h4 className="text-base md:text-lg font-bold text-white
                group-hover:text-cyan-300 transition-colors leading-tight">
                {project.name}
              </h4>
              <span className="flex-shrink-0 mt-0.5 w-7 h-7 flex items-center justify-center
                rounded-full border border-white/10 bg-white/5 text-slate-500
                group-hover:border-cyan-400/40 group-hover:text-cyan-400 transition-all">
                <ChevronRight size={13} />
              </span>
            </div>

            {/* description */}
            <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 mb-4 flex-1">
              {project.description}
            </p>

            {/* tags */}
            <div className="flex flex-wrap gap-1.5">
              {project.tech.slice(0, 4).map((t, i) => (
                <span key={i}
                  className="text-[11px] px-2.5 py-1 rounded-lg font-medium
                    bg-white/5 border border-white/8 text-slate-400
                    group-hover:border-cyan-400/20 group-hover:text-cyan-300/70
                    transition-colors">
                  {t}
                </span>
              ))}
              {project.tech.length > 4 && (
                <span className="text-[11px] px-2.5 py-1 rounded-lg font-medium
                  bg-white/5 border border-white/8 text-slate-600">
                  +{project.tech.length - 4} more
                </span>
              )}
            </div>
          </div>

          {/* hover accent line */}
          <div className="absolute inset-x-0 bottom-0 h-[1.5px]
            bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-purple-600
            scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-400" />
        </div>
      </Link>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   ANIMATED SKILL BAR
───────────────────────────────────────────── */
/* Career started in 2019 — the counter derives from the current year so it never goes stale. */
const CAREER_START_YEAR = 2019;
const YEARS_EXPERIENCE = new Date().getFullYear() - CAREER_START_YEAR;

const LEVEL_LABEL = (n) =>
  n >= 90 ? "Expert" : n >= 80 ? "Advanced" : n >= 70 ? "Intermediate" : "Familiar";
const LEVEL_COLOR = (n) =>
  n >= 90 ? "text-cyan-300 bg-cyan-400/10 border-cyan-400/25"
  : n >= 80 ? "text-blue-300 bg-blue-400/10 border-blue-400/25"
  : n >= 70 ? "text-fuchsia-300 bg-fuchsia-400/10 border-fuchsia-400/25"
  : "text-slate-400 bg-white/5 border-white/10";

function SkillBar({ name, level, color = "from-cyan-400 to-fuchsia-500", delay = 0 }) {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setWidth(level); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [level]);

  return (
    <div ref={ref} className="mb-4">
      <div className="flex justify-between items-center mb-1.5 gap-2">
        <span className="text-sm text-slate-200 font-medium leading-tight">{name}</span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-md border ${LEVEL_COLOR(level)}`}>
            {LEVEL_LABEL(level)}
          </span>
          <span className="text-[11px] font-mono text-slate-500 w-8 text-right">{level}%</span>
        </div>
      </div>
      <div className="relative h-1.5 w-full bg-white/[0.07] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${color} transition-all ease-out`}
          style={{
            width: `${width}%`,
            transitionDuration: `${0.85 + delay * 0.08}s`,
            boxShadow: width > 0 ? '0 0 8px -1px rgba(56,189,248,0.5)' : 'none',
          }}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COUNTER CARD
───────────────────────────────────────────── */
function CounterCard({ value, suffix, label, delay }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const duration = 1400;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) { start = value; clearInterval(timer); }
      setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible, value]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      className="flex flex-col items-center gap-1 p-6 rounded-2xl
        bg-white/[0.04] border border-white/10
        hover:border-cyan-400/30 transition-colors"
    >
      <span className="text-4xl font-bold font-mono bg-gradient-to-r from-cyan-400 to-fuchsia-400 bg-clip-text text-transparent">
        {count}{suffix}
      </span>
      <span className="text-xs text-slate-400 text-center leading-tight mt-1">{label}</span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────── */
function SectionHeader({ label, title, subtitle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      className="text-center mb-14"
    >
      <span className="inline-block font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-3">
        {label}
      </span>
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">{title}</h2>
      {subtitle && <p className="text-slate-400 max-w-xl mx-auto text-sm leading-relaxed">{subtitle}</p>}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   TYPEWRITER HOOK
───────────────────────────────────────────── */
function useTypewriter(words, speed = 75, pause = 2200) {
  const [display, setDisplay] = useState("");
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIdx];
    let t;
    if (!deleting && charIdx < current.length)
      t = setTimeout(() => setCharIdx(c => c + 1), speed);
    else if (!deleting && charIdx === current.length)
      t = setTimeout(() => setDeleting(true), pause);
    else if (deleting && charIdx > 0)
      t = setTimeout(() => setCharIdx(c => c - 1), speed / 2);
    else {
      setDeleting(false);
      setWordIdx(i => (i + 1) % words.length);
    }
    setDisplay(current.substring(0, charIdx));
    return () => clearTimeout(t);
  }, [charIdx, deleting, wordIdx, words, speed, pause]);

  return display;
}

/* ─────────────────────────────────────────────
   STAR BACKGROUND
───────────────────────────────────────────── */
function StarField() {
  const stars = useRef(
    Array.from({ length: 180 }, () => ({
      size: Math.random() * 2 + 1,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: 2 + Math.random() * 3,
      delay: Math.random() * 3,
    }))
  );

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      {stars.current.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            width: s.size, height: s.size,
            left: `${s.left}%`, top: `${s.top}%`,
            opacity: 0.5,
            animation: `twinkle ${s.duration}s ${s.delay}s ease-in-out infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%,100%{opacity:.15;transform:scale(1)}
          50%{opacity:.9;transform:scale(1.4)}
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PORTFOLIO
───────────────────────────────────────────── */
export default function Portfolio() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const [toastStatus, setToastStatus] = useState(null);
  const [toastText, setToastText] = useState("");
  const [sending, setSending] = useState(false);
  const formOpenedAt = useRef(Date.now());
  const typed = useTypewriter(["Flutter Developer", "React Developer", "Firebase Expert", "UI/UX Designer"]);

  const navLinks = [
    { label: "Home",         id: "#home" },
    { label: "Skills",       id: "#skills" },
    { label: "Services",     id: "#services" },
    { label: "Achievements", id: "#achievements" },
    { label: "Certifications", id: "#certifications" },
    { label: "Projects",     id: "#projects" },
    { label: "Contact",      id: "#contact" },
  ];

  const projects = [
    {
      name: "Caffezo – Smart Restaurant Platform",
      folder: "Caffezo",
      count: 8,
      live: "https://caffezo.com",
      description:
        "A multi-tenant SaaS platform for restaurants and bars across Europe. QR digital menus, order management, staff shifts with automatic payroll, table booking and analytics — running live in 8 languages on subscription tiers.",
      tech: ["React", "Flutter", "Firebase", "Multi-tenant SaaS", "8 Languages", "Subscriptions", "Analytics", "PDF Reports"],
    },
    {
      name: "ManB2B – Field Team Management",
      folder: "Management",
      count: 4,
      live: "https://manp2p.web.app",
      description:
        "A production platform for companies running field teams: recurring schedules, start/stop hour tracking, photo reports, three role-based views and an automatic monthly PDF invoice. Four languages with full right-to-left support.",
      tech: ["React", "Firebase", "Role-Based Access", "Time Tracking", "PDF Invoicing", "4 Languages", "RTL", "Real-Time Sync"],
    },
    {
      name: "Sì Pattina – Live Group Ride Tracking",
      folder: "SiPattina",
      count: 5,
      live: "https://si-pattini.web.app/welcome",
      description:
        "A live GPS platform for city skating groups. One phone records the route while everyone else follows the line drawing itself on the map in real time, then the ride becomes a shareable card, sticker, video or GPX file.",
      tech: ["React", "Firebase", "Live GPS Tracking", "Interactive Maps", "GPX Export", "On-Device Rendering", "Capacitor", "Italian UI"],
    },
    {
      name: "PocketLinks",
      folder: "PocketLinks",
      playStore: "https://play.google.com/store/apps/details?id=com.auryxstudio.pocketlinks",
      count: 4,
      description: "Smart link manager that saves, organizes, and manages links from any app using Android share intent. Fully offline.",
      tech: ["Flutter", "Dart", "SQLite", "Android Share Intent", "Offline Storage", "Clean UI"],
    },
    {
      name: "Nmoney",
      folder: "nmoney",
      playStore: "https://play.google.com/store/apps/details?id=com.auryxstudio.nmoney",
      count: 4,
      description: "Smart money manager for tracking income, expenses, and jobs with hourly-rate support and clean financial summaries.",
      tech: ["Flutter", "Dart", "Firebase", "Local Storage", "State Management", "Clean Architecture"],
    },
    {
      name: "Church Community App",
      folder: "church",
      count: 4,
      description: "A modern community app for church members with events, media, announcements, and member profiles.",
      tech: ["Flutter", "Firebase", "Authentication", "Cloud Storage"],
    },
    {
      name: "TozBest – Task Manager",
      folder: "tozbest",
      count: 3,
      description: "Collaborative task manager to track goals, share progress, and keep friends motivated with real-time updates.",
      tech: ["Flutter", "Firebase Realtime DB", "Push Notifications"],
    },
    {
      name: "Modern 3D Concept Design",
      folder: "modern3d",
      count: 8,
      description: "A futuristic 3D animated app concept with smooth transitions across chat, e-shop, and profile screens.",
      tech: ["Flutter", "Rive Animations", "UI/UX Design"],
    },
  ];

  const services = [
    { icon: <Smartphone size={22} />, title: "Mobile App Development",          desc: "High-performance Flutter apps for Android & iOS with clean architecture." },
    { icon: <Globe size={22} />,      title: "React Web Development",            desc: "Responsive, fast-loading websites and business dashboards with React & Tailwind." },
    { icon: <Code2 size={22} />,      title: "React Portfolio Websites",         desc: "Modern animated portfolios built with React, TailwindCSS, and Framer Motion." },
    { icon: <Building2 size={22} />,  title: "Business Management Systems",     desc: "Custom apps for managing staff, products, payroll, and operations." },
    { icon: <Database size={22} />,   title: "Firebase Integration",             desc: "Real-time database, authentication, cloud functions, and storage setup." },
    { icon: <Palette size={22} />,    title: "UI/UX Design",                     desc: "Intuitive, modern interface design with Figma — mobile and web." },
    { icon: <Wrench size={22} />,     title: "App Architecture",                 desc: "Clean, scalable code architecture and efficient state management." },
    { icon: <RefreshCw size={22} />,  title: "Maintenance & Updates",            desc: "Ongoing support, bug fixing, and feature enhancements for existing apps." },
  ];

  const certifications = [
    {
      issuer: "HackerRank",
      title: "React (Basic)",
      date: "September 2026",
      desc: "Component design, state management, event handling, and routing — assessed by timed coding challenges.",
      url: "https://www.hackerrank.com/certificates/iframe/a15241a0c606",
    },
    {
      issuer: "HackerRank",
      title: "JavaScript (Intermediate)",
      date: "September 2026",
      desc: "Closures, asynchronous patterns, ES6+, error handling, and design patterns — assessed by timed coding challenges.",
      url: "https://www.hackerrank.com/certificates/iframe/3cb2587e2db3",
    },
    {
      issuer: "Microsoft · Certiport",
      title: "MTA: Introduction to Programming using Java",
      date: "June 2021",
      desc: "Proctored Microsoft Technology Associate exam — Java syntax, OOP, data types, control flow, and exception handling. Verification code J3ns-4TWx.",
      url: "https://verify.certiport.com",
    },
  ];

  const training = [
    { issuer: "Oracle Academy", title: "PL/SQL Database Programming, Semester 2", date: "December 2022" },
    { issuer: "Oracle Academy", title: "Database Design",                          date: "January 2022" },
    { issuer: "Oracle Academy", title: "Database Foundations",                     date: "January 2022" },
    { issuer: "Oracle Academy", title: "Database Programming with SQL",            date: "December 2021" },
    { issuer: "Oracle Academy", title: "Java Fundamentals",                        date: "April 2021" },
    { issuer: "AFRO Academy",   title: "ICT Program — Technical Training",       date: "2024" },
  ];

  /* ── Navbar scroll detection ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Active section detection ── */
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { threshold: 0.4 }
    );
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  /* ── Body scroll lock when menu open ── */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const scrollTo = useCallback((id) => {
    setMenuOpen(false);
    setTimeout(() => {
      const el = document.querySelector(id);
      const nav = document.querySelector("nav");
      if (!el) return;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (nav?.offsetHeight || 0) - 8, behavior: "smooth" });
    }, menuOpen ? 320 : 0);
  }, [menuOpen]);

  /* ── Contact form ── */
  const showToast = useCallback((status, text) => {
    setToastStatus(status);
    setToastText(text);
    setTimeout(() => setToastStatus(null), 5000);
  }, []);

  const handleContact = useCallback(async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = {
      name: form.name.value.trim(),
      email: form.email.value.trim(),
      phone: form.phone.value.trim(),
      message: form.message.value.trim(),
    };

    // Bots fill the hidden field or submit within seconds of page load: pretend it worked.
    if (form.website.value || Date.now() - formOpenedAt.current < 3000) {
      form.reset();
      showToast("success", "Message sent! I'll reply soon.");
      return;
    }
    if (!data.email && !data.phone) {
      showToast("error", "Please add your email or phone number so I can reply.");
      return;
    }
    if (data.phone && !/^\+?[0-9 ()./-]{7,24}$/.test(data.phone)) {
      showToast("error", "That phone number doesn't look right.");
      return;
    }
    if (data.message.length < 10) {
      showToast("error", "Please write a few more words about your project.");
      return;
    }

    setSending(true);
    try {
      const { sendContactMessage } = await import("./contactService");
      await sendContactMessage(data);
      form.reset();
      showToast("success", "Message sent! I'll reply soon.");
    } catch (err) {
      showToast("error", err?.code === "permission-denied"
        ? "Please wait a minute before sending another message."
        : "Failed to send. Try again.");
    } finally {
      setSending(false);
    }
  }, [showToast]);

  return (
    <div className="relative bg-[#050816] min-h-screen text-white overflow-x-hidden">
      <StarField />

      {/* ══════════════ NAVBAR ══════════════ */}
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 ${
        scrolled ? "bg-[#050816]/80 backdrop-blur-xl border-b border-white/8 shadow-[0_0_30px_-10px_rgba(56,189,248,0.15)]" : ""
      }`}>
        <div className="max-w-6xl mx-auto flex justify-between items-center px-6 py-4">
          <button
            onClick={() => scrollTo("#home")}
            className="font-mono text-base font-bold tracking-wide"
          >
            <span className="text-cyan-400">&lt;</span>
            <span className="text-white">KM</span>
            <span className="text-cyan-400">/&gt;</span>
          </button>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  activeSection === item.id.slice(1)
                    ? "text-cyan-400 bg-cyan-400/10"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <a
            href="https://github.com/KeroLosMina15"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg
              border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition-all"
          >
            <Github size={15} /> GitHub
          </a>

          {/* Mobile burger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-3.5 flex flex-col justify-between">
              <span className={`block h-0.5 bg-current rounded transition-all ${menuOpen ? "rotate-45 translate-y-1.5" : ""}`} />
              <span className={`block h-0.5 bg-current rounded transition-all ${menuOpen ? "opacity-0 scale-x-0" : ""}`} />
              <span className={`block h-0.5 bg-current rounded transition-all ${menuOpen ? "-rotate-45 -translate-y-1.5" : ""}`} />
            </div>
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.28 }}
              className="md:hidden overflow-hidden bg-[#070b1a]/95 backdrop-blur-xl border-t border-white/8"
            >
              <div className="flex flex-col px-6 py-4 gap-1">
                {navLinks.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      activeSection === item.id.slice(1)
                        ? "text-cyan-400 bg-cyan-400/10"
                        : "text-slate-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ══════════════ HERO ══════════════ */}
      <section id="home" className="min-h-screen flex items-center pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-6 w-full">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-20">

            {/* Profile image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="relative flex-shrink-0"
            >
              <div className="w-48 h-48 md:w-64 md:h-64 rounded-full p-[3px] bg-gradient-to-br from-cyan-400 via-fuchsia-500 to-purple-600">
                <div className="w-full h-full rounded-full overflow-hidden bg-[#050816]">
                  <img
                    src={require("./assets/Profile/Profile.jpg")}
                    alt="Kerolos Mina"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              {/* Glow ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400/20 to-purple-600/20 blur-2xl -z-10 scale-110" />
              {/* Available dot */}
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-[#050816]/90 border border-white/10 px-2.5 py-1 rounded-full">
                <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_#34d399] animate-pulse" />
                <span className="text-[11px] text-emerald-300 font-medium">Available</span>
              </div>
            </motion.div>

            {/* Text content */}
            <div className="flex-1 text-center md:text-left">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
              >
                <p className="text-slate-400 text-sm font-medium mb-2 tracking-wide">Hi, I'm</p>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-3
                  bg-gradient-to-r from-white via-cyan-100 to-cyan-400 bg-clip-text text-transparent">
                  Kerolos Mina
                </h1>

                <div className="flex items-center gap-2 justify-center md:justify-start mb-6 h-8">
                  <span className="text-cyan-400 font-mono text-lg">$</span>
                  <span className="text-lg md:text-xl font-semibold text-slate-200">{typed}</span>
                  <span className="w-0.5 h-6 bg-cyan-400 animate-pulse" />
                </div>

                <p className="text-slate-400 leading-relaxed mb-4 max-w-lg">
                  I'm a <span className="text-cyan-300 font-semibold">Mobile App & Web Developer</span> focused
                  on crafting modern, high-performance applications using{" "}
                  <span className="text-fuchsia-400 font-medium">Flutter</span> and{" "}
                  <span className="text-cyan-400 font-medium">React</span>.
                </p>
                <p className="text-slate-400 leading-relaxed mb-8 max-w-lg text-sm">
                  From scalable mobile systems to animated web experiences — I turn ideas into clean,
                  powerful digital products that{" "}
                  <span className="text-cyan-300 font-medium">inspire users and empower businesses.</span>
                </p>

                <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-8">
                  {["Problem Solver", "Creative Thinker", "Clean Code", "UI/UX Focused", "Always Evolving"].map((trait) => (
                    <span key={trait}
                      className="text-xs px-3 py-1.5 rounded-full border border-cyan-400/30
                        text-cyan-300/80 bg-cyan-400/5 backdrop-blur-sm">
                      {trait}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <button
                    onClick={() => scrollTo("#projects")}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm
                      bg-gradient-to-r from-cyan-500 to-fuchsia-600
                      hover:from-cyan-400 hover:to-fuchsia-500
                      shadow-[0_0_20px_-6px_rgba(56,189,248,0.5)]
                      transition-all duration-300"
                  >
                    View Projects <ChevronRight size={15} />
                  </button>
                  <button
                    onClick={() => scrollTo("#contact")}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm
                      border border-white/15 text-slate-300 hover:text-white
                      hover:bg-white/5 hover:border-white/25 transition-all"
                  >
                    Contact Me
                  </button>
                  <a
                    href="https://github.com/KeroLosMina15"
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm
                      border border-white/10 text-slate-400 hover:text-white
                      hover:bg-white/5 transition-all"
                  >
                    <Github size={16} />
                  </a>
                  <a
                    href="https://www.linkedin.com/in/kerolos-mina-484801312"
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm
                      border border-white/10 text-slate-400 hover:text-white
                      hover:bg-white/5 transition-all"
                  >
                    <Linkedin size={16} />
                  </a> 
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ SKILLS ══════════════ */}
      <section id="skills" className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            label="02. Expertise"
            title="Skills & Stack"
            subtitle="Technologies and tools I use to build great products"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                label: "Mobile & App Dev",
                sublabel: "Flutter · Firebase · Android",
                color: "from-cyan-400 to-blue-500",
                skills: [
                  { name: "Flutter & Dart",                   level: 90 },
                  { name: "Firebase (Auth, DB, Storage)",      level: 85 },
                  { name: "Java",                              level: 80 },
                  { name: "State Mgmt (BLoC / Provider)",      level: 82 },
                  { name: "API Integration & REST",            level: 80 },
                  { name: "Clean Architecture & OOP",          level: 85 },
                  { name: "SQLite & Offline Storage",          level: 80 },
                ],
              },
              {
                label: "Web & Databases",
                sublabel: "React · SQL · Python",
                color: "from-fuchsia-400 to-pink-500",
                skills: [
                  { name: "React.js / JavaScript",            level: 85 },
                  { name: "HTML5 / CSS3 / Tailwind CSS",      level: 88 },
                  { name: "SQL / Oracle PL/SQL",              level: 75 },
                  { name: "Python",                           level: 60 },
                  { name: "Responsive UI & Animations",       level: 88 },
                  { name: "Framer Motion / Rive",             level: 78 },
                ],
              },
              {
                label: "Design & Tools",
                sublabel: "Figma · Git · Creative",
                color: "from-purple-400 to-indigo-500",
                skills: [
                  { name: "UI/UX Design (Figma)",             level: 80 },
                  { name: "Git / GitHub",                      level: 85 },
                  { name: "VS Code / Android Studio",          level: 90 },
                  { name: "Postman / REST Testing",            level: 78 },
                  { name: "Video Editing",                     level: 75 },
                  { name: "Logo & Brand Design",               level: 70 },
                ],
              },
            ].map((cat, ci) => (
              <motion.div
                key={cat.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: ci * 0.12, duration: 0.5 }}
                className="bg-white/[0.04] border border-white/10 rounded-2xl p-6
                  hover:border-white/20 transition-colors"
              >
                <div className="mb-5">
                  <div className={`inline-block text-xs font-mono font-semibold uppercase tracking-widest
                    px-3 py-1 rounded-full bg-gradient-to-r ${cat.color} bg-clip-text text-transparent
                    border border-white/10`}>
                    {cat.label}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5 font-mono">{cat.sublabel}</p>
                </div>
                {cat.skills.map((s, si) => (
                  <SkillBar key={s.name} name={s.name} level={s.level} color={cat.color} delay={si} />
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ SERVICES ══════════════ */}
      <section id="services" className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            label="03. What I Do"
            title="Services I Offer"
            subtitle="End-to-end solutions for mobile apps, web platforms, and creative tech"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ delay: i * 0.07 }}
                className="group p-5 rounded-2xl bg-white/[0.04] border border-white/10
                  hover:border-cyan-400/35 hover:bg-white/[0.07]
                  transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20
                  flex items-center justify-center text-cyan-400 mb-4
                  group-hover:bg-cyan-400/15 transition-colors">
                  {s.icon}
                </div>
                <h4 className="text-sm font-semibold text-white mb-2 leading-tight">{s.title}</h4>
                <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ ACHIEVEMENTS ══════════════ */}
      <section id="achievements" className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            label="04. Numbers"
            title="Achievements & Milestones"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { value: 10,  suffix: "+", label: "Mobile Apps\nDeveloped" },
              { value: 8,   suffix: "+", label: "React Web\nDesigns" },
              { value: 4,   suffix: "+", label: "Business\nClients" },
              { value: YEARS_EXPERIENCE, suffix: "+", label: "Years\nExperience" },
              { value: 100, suffix: "%", label: "Project\nSatisfaction" },
            ].map((item, i) => (
              <CounterCard key={i} value={item.value} suffix={item.suffix} label={item.label} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ CERTIFICATIONS ══════════════ */}
      <section id="certifications" className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            label="05. Credentials"
            title="Verified Certifications"
            subtitle="Independently assessed skills — each certificate links to its official verification page"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {certifications.map((c, i) => (
              <motion.a
                key={i}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ delay: i * 0.07 }}
                className="group flex flex-col p-5 rounded-2xl bg-white/[0.04] border border-white/10
                  hover:border-cyan-400/35 hover:bg-white/[0.07]
                  transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20
                    flex items-center justify-center text-cyan-400
                    group-hover:bg-cyan-400/15 transition-colors">
                    <Award size={22} />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 pt-1">{c.date}</span>
                </div>
                <p className="text-[11px] uppercase tracking-wider text-cyan-400/80 mb-1">{c.issuer}</p>
                <h4 className="text-sm font-semibold text-white mb-2 leading-tight">{c.title}</h4>
                <p className="text-xs text-slate-400 leading-relaxed mb-4">{c.desc}</p>
                <span className="mt-auto text-xs font-medium text-slate-300
                  group-hover:text-cyan-400 transition-colors">
                  Verify certificate →
                </span>
              </motion.a>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-white/10">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1">
              Course Final Exams &amp; Training
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Awards for satisfactory completion of course final exams
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {training.map((t, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3 text-xs
                  border-b border-white/[0.06] pb-2">
                  <span className="text-slate-300">{t.title}</span>
                  <span className="text-slate-500 whitespace-nowrap">{t.issuer} · {t.date}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Certificates are issued under my legal name, <span className="text-slate-400">Kerolos Felobos</span> — the
            Microsoft certificate under <span className="text-slate-400">Kerolos Mina Mounir</span> (verify with code J3ns-4TWx).
          </p>
        </div>
      </section>

      {/* ══════════════ PROJECTS ══════════════ */}
      <section id="projects" className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            label="06. Work"
            title="Featured Projects"
            subtitle="Real apps built and shipped — from restaurant platforms to finance tools"
          />
          <div className="flex flex-col gap-5">
            {projects.map((project, i) => (
              <ProjectCard key={i} project={project} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ CONTACT ══════════════ */}
      <section id="contact" className="py-28">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeader
            label="07. Say Hello"
            title="Let's Work Together"
            subtitle="Have a project in mind or want to collaborate? I'd love to hear from you."
          />

          <div className="grid md:grid-cols-2 gap-10 items-start">
            {/* Left info */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-slate-400 leading-relaxed mb-8">
                I'm currently available for freelance projects and full-time opportunities.
                Whether it's a mobile app, a React website, or a business system —
                let's build something great together.
              </p>

              <div className="flex flex-col gap-4 mb-8">
                <a
                  href="https://github.com/KeroLosMina15"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.04]
                    border border-white/10 hover:border-cyan-400/30
                    text-slate-300 hover:text-white transition-all group"
                >
                  <Github size={18} className="text-cyan-400" />
                  <div>
                    <p className="text-xs text-slate-500 font-mono mb-0.5">GitHub</p>
                    <p className="text-sm font-medium">KeroLosMina15</p>
                  </div>
                  <ChevronRight size={14} className="ml-auto text-slate-600 group-hover:text-cyan-400 transition-colors" />
                </a>
                <a
                  href="https://www.linkedin.com/in/kerolos-mina-484801312"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.04]
                    border border-white/10 hover:border-cyan-400/30
                    text-slate-300 hover:text-white transition-all group"
                >
                  <Linkedin size={18} className="text-cyan-400" />
                  <div>
                    <p className="text-xs text-slate-500 font-mono mb-0.5">LinkedIn</p>
                    <p className="text-sm font-medium">Kerolos Mina</p>
                  </div>
                  <ChevronRight size={14} className="ml-auto text-slate-600 group-hover:text-cyan-400 transition-colors" />
                </a>
              </div>

              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399] animate-pulse" />
                <span className="text-sm text-emerald-300 font-medium">Open to new opportunities</span>
              </div>
            </motion.div>

            {/* Right form */}
            <motion.form
              onSubmit={handleContact}
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-6 flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-mono text-slate-500 uppercase tracking-wider">Name</label>
                <input
                  name="name" type="text" required maxLength={100} placeholder="Your name"
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm
                    text-white placeholder-slate-600 outline-none
                    focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/20 transition-all"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-mono text-slate-500 uppercase tracking-wider">Email</label>
                  <input
                    name="email" type="email" maxLength={200} placeholder="your@email.com"
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm
                      text-white placeholder-slate-600 outline-none
                      focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/20 transition-all"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-mono text-slate-500 uppercase tracking-wider">Phone</label>
                  <input
                    name="phone" type="tel" maxLength={24} placeholder="+39 ..."
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm
                      text-white placeholder-slate-600 outline-none
                      focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/20 transition-all"
                  />
                </div>
              </div>
              <p className="-mt-2 text-xs text-slate-500">Email or phone — at least one, so I can reply.</p>
              {/* Honeypot: hidden from people, filled in by bots */}
              <input name="website" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
                className="absolute -left-[9999px] w-px h-px opacity-0" />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-mono text-slate-500 uppercase tracking-wider">Message</label>
                <textarea
                  name="message" required minLength={10} maxLength={3000} rows={5} placeholder="Tell me about your project..."
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm
                    text-white placeholder-slate-600 outline-none resize-none
                    focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/20 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm
                  disabled:opacity-60 disabled:cursor-wait
                  bg-gradient-to-r from-cyan-500 to-fuchsia-600
                  hover:from-cyan-400 hover:to-fuchsia-500
                  shadow-[0_0_20px_-6px_rgba(56,189,248,0.4)]
                  transition-all duration-300"
              >
                <Send size={15} /> {sending ? "Sending..." : "Send Message"}
              </button>
            </motion.form>
          </div>

          <p className="text-center text-xs text-slate-600 mt-16">
            © {new Date().getFullYear()} Kerolos Mina. All rights reserved.
          </p>
        </div>
      </section>

      {/* ══════════════ TOAST ══════════════ */}
      <AnimatePresence>
        {toastStatus && (
          <motion.div
            initial={{ opacity: 0, y: 24, x: 24 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: 24 }}
            className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5
              rounded-2xl border backdrop-blur-xl shadow-2xl
              ${toastStatus === "success"
                ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-300"
                : "bg-red-500/15 border-red-400/30 text-red-300"
              }`}
          >
            <span className="text-lg">{toastStatus === "success" ? "✅" : "❌"}</span>
            <span className="text-sm font-medium">
              {toastText}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
